import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, tap } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AuthenticationService } from "../../api/services/authentication.service";
import { UserDto } from "../../api/models/user-dto";
import { LoginStatusDto } from "../../api/models/login-status-dto";
import { Ability, AbilityClass, createAliasResolver, PureAbility } from "@casl/ability";
import { CaslAction } from "../../server/enums/casl-action.enum";
import { EntityTyp } from "../enums/entity-typ.enum";
import { unpackRules } from "@casl/ability/extra";

export type AppAbility = Ability<[CaslAction, EntityTyp]>;
export const AppAbility = Ability as AbilityClass<AppAbility>;
export const abilityProviders = [
  {
    provide: AppAbility,
    useValue: new AppAbility([], {
      resolveAction: createAliasResolver({
        [CaslAction.Modify]: [CaslAction.Update, CaslAction.Delete],
        [CaslAction.CRUD]: [
          CaslAction.Create,
          CaslAction.Read,
          CaslAction.List,
          CaslAction.Update,
          CaslAction.Delete,
          CaslAction.Restore,
        ],
        [CaslAction.ReadAndList]: [CaslAction.Read, CaslAction.List],
      }),
    }),
  },
  { provide: PureAbility, useExisting: AppAbility },
];

@Injectable({ providedIn: "root" })
export class AuthService {
  public currentUser: Observable<UserDto | null>;

  private readonly currentUserSubject: BehaviorSubject<UserDto | null>;

  constructor(
    private authenticationService: AuthenticationService,
    private ability: AppAbility
  ) {
    this.currentUserSubject = new BehaviorSubject<UserDto | null>(
      JSON.parse(<string>localStorage.getItem("currentUser"))
    );
    const rules = JSON.parse(<string>localStorage.getItem("currentRules"));
    if (rules?.length) {
      this.setAbilities(rules);
    }
    this.currentUser = this.currentUserSubject.asObservable();
  }

  // getter: currentUserValue
  public get currentUserValue(): UserDto | null {
    return this.currentUserSubject.value;
  }

  /**
   * User login
   *
   * @param email
   * @param password
   * @returns user
   */
  login(email: string, password: string): Observable<UserDto> {
    return this.authenticationService.authControllerLogin({ body: { email, password } }).pipe(
      map((authResult) => {
        return this.setAuthResult(authResult);
      })
    );
  }

  setAuthResult(authResult: LoginStatusDto) {
    const user: UserDto = authResult.user;
    const rules = authResult.rules;
    localStorage.setItem("currentUser", JSON.stringify(user));
    localStorage.setItem("currentRules", JSON.stringify(rules));
    this.setAbilities(rules);
    // notify
    this.currentUserSubject.next(user);
    return user;
  }

  /**
   * User logout
   *
   */
  logout(reload = false) {
    // remove user from local storage to log user out
    localStorage.removeItem("currentUser");
    localStorage.removeItem("currentRules");
    this.ability.update([]);

    this.authenticationService.authControllerLogout().subscribe(() => {
      // notify
      this.currentUserSubject.next(null);
    });

    if (reload) {
      window.location.reload();
    }
  }

  refreshAccessToken() {
    return this.authenticationService.authControllerCreateAccessTokenFromRefreshToken().pipe(
      tap((tokens) => {
        this.setAuthResult(tokens);
      }),
      catchError((error) => {
        this.logout(true);
        return of(false);
      })
    );
  }

  /* CASL */
  private setAbilities(rules: any) {
    this.ability.update(unpackRules(rules));
  }

  get vertretenByString(): string {
    return this.currentUserValue?.vertretenBy?.email ?? "";
  }

  loginAs(id: number) {
    this.authenticationService.authControllerLoginAs({ id }).subscribe((authResult) => {
      this.setAuthResult(authResult);
      window.location.href = "/";
    });
  }

  returnToMainUser() {
    this.loginAs(this.currentUserValue.vertretenBy.id);
  }
}
